import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Divider
} from '@mui/material'
import {
  Gavel,
  Edit,
  Delete,
  Search,
  Add,
  CloudUpload,
  Download,
  Visibility,
  PictureAsPdf,
  Description,
  Link,
  AttachFile
} from '@mui/icons-material'
import axios from 'axios'

const NormatividadAdmin = () => {
  const [documentos, setDocumentos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategoria, setSelectedCategoria] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploadMode, setUploadMode] = useState('file') // 'file' o 'url'
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    categoria: 'ley',
    numero: '',
    fecha_expedicion: '',
    entidad_emisora: '',
    archivo: null,
    pdf_url: ''
  })

  // Estado para el modal de vista previa del PDF
  const [openPdfModal, setOpenPdfModal] = useState(false)
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null)

  const categorias = [
    { value: 'ley', label: 'Ley' },
    { value: 'decreto', label: 'Decreto' },
    { value: 'resolucion', label: 'Resolución' },
    { value: 'circular', label: 'Circular' },
    { value: 'directiva', label: 'Directiva' },
    { value: 'norma_tecnica', label: 'Norma Técnica' },
    { value: 'lineamiento', label: 'Lineamiento' },
    { value: 'politica', label: 'Política' }
  ]

  useEffect(() => {
    loadDocumentos()
  }, [])

  const loadDocumentos = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/normatividad', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setDocumentos(response.data)
    } catch (error) {
      console.error('Error:', error)
      // Datos de ejemplo mientras se implementa el backend
      setDocumentos([
        {
          id: 1,
          titulo: 'Ley 1324 de 2009',
          descripcion: 'Por la cual se fijan parámetros y criterios para organizar el sistema de evaluación de resultados de la calidad de la educación',
          categoria: 'ley',
          numero: '1324',
          fecha_expedicion: '2009-07-13',
          entidad_emisora: 'Congreso de la República',
          archivo_url: '/uploads/normatividad/ley_1324_2009.pdf',
          archivo_nombre: 'ley_1324_2009.pdf',
          archivo_tamano: 245678
        },
        {
          id: 2,
          titulo: 'Decreto 4904 de 2009',
          descripcion: 'Por el cual se reglamenta la organización, oferta y funcionamiento de la prestación del servicio educativo para el trabajo y el desarrollo humano',
          categoria: 'decreto',
          numero: '4904',
          fecha_expedicion: '2009-12-16',
          entidad_emisora: 'Ministerio de Educación Nacional',
          archivo_url: '/uploads/normatividad/decreto_4904_2009.pdf',
          archivo_nombre: 'decreto_4904_2009.pdf',
          archivo_tamano: 189234
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedDoc(null)
    setUploadMode('file')
    setFormData({
      titulo: '',
      descripcion: '',
      categoria: 'ley',
      numero: '',
      fecha_expedicion: '',
      entidad_emisora: '',
      archivo: null,
      pdf_url: ''
    })
    setOpenDialog(true)
  }

  const handleEdit = (doc) => {
    setSelectedDoc(doc)
    setUploadMode('file')
    setFormData({
      titulo: doc.titulo,
      descripcion: doc.descripcion,
      categoria: doc.categoria,
      numero: doc.numero,
      fecha_expedicion: doc.fecha_expedicion,
      entidad_emisora: doc.entidad_emisora,
      archivo: null,
      pdf_url: ''
    })
    setOpenDialog(true)
  }

  const handleSave = async () => {
    try {
      setUploading(true)
      const token = localStorage.getItem('sgc_token')
      
      if (selectedDoc) {
        // Editar documento existente
        const formDataToSend = new FormData()

        // Agregar campos obligatorios
        formDataToSend.append('titulo', formData.titulo)
        formDataToSend.append('categoria', formData.categoria)
        formDataToSend.append('activo', 'true')

        // Agregar campos opcionales solo si tienen valor
        if (formData.descripcion && formData.descripcion.trim()) {
          formDataToSend.append('descripcion', formData.descripcion)
        }
        if (formData.numero && formData.numero.trim()) {
          formDataToSend.append('numero', formData.numero)
        }
        if (formData.fecha_expedicion && formData.fecha_expedicion.trim()) {
          formDataToSend.append('fecha_expedicion', formData.fecha_expedicion)
        }
        if (formData.entidad_emisora && formData.entidad_emisora.trim()) {
          formDataToSend.append('entidad_emisora', formData.entidad_emisora)
        }
        if (formData.archivo) {
          formDataToSend.append('archivo', formData.archivo)
        }

        await axios.put(`/api/normatividad/${selectedDoc.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(progress)
          }
        })
        setSuccess('Documento actualizado exitosamente')
      } else {
        // Crear nuevo
        if (uploadMode === 'url') {
          // Crear desde URL
          const dataToSend = {
            titulo: formData.titulo,
            descripcion: formData.descripcion,
            categoria: formData.categoria,
            numero: formData.numero,
            fecha_expedicion: formData.fecha_expedicion,
            entidad_emisora: formData.entidad_emisora,
            pdf_url: formData.pdf_url
          }
          
          await axios.post('/api/normatividad/from-url', dataToSend, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          setSuccess('Documento descargado y creado exitosamente')
        } else {
          // Crear desde archivo
          const formDataToSend = new FormData()

          // Agregar campos obligatorios
          formDataToSend.append('titulo', formData.titulo)
          formDataToSend.append('categoria', formData.categoria)

          // Agregar campos opcionales solo si tienen valor
          if (formData.descripcion && formData.descripcion.trim()) {
            formDataToSend.append('descripcion', formData.descripcion)
          }
          if (formData.numero && formData.numero.trim()) {
            formDataToSend.append('numero', formData.numero)
          }
          if (formData.fecha_expedicion && formData.fecha_expedicion.trim()) {
            formDataToSend.append('fecha_expedicion', formData.fecha_expedicion)
          }
          if (formData.entidad_emisora && formData.entidad_emisora.trim()) {
            formDataToSend.append('entidad_emisora', formData.entidad_emisora)
          }
          if (formData.archivo) {
            formDataToSend.append('archivo', formData.archivo)
          }

          await axios.post('/api/normatividad', formDataToSend, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              setUploadProgress(progress)
            }
          })
          setSuccess('Documento creado exitosamente')
        }
      }
      
      setOpenDialog(false)
      loadDocumentos()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleViewPdf = (doc) => {
    setSelectedPdfDoc(doc)
    setOpenPdfModal(true)
  }

  const handleDelete = async (doc) => {
    if (!window.confirm(`¿Está seguro de eliminar el documento "${doc.titulo}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/normatividad/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Documento eliminado exitosamente')
      loadDocumentos()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const getCategoriaLabel = (categoria) => {
    const cat = categorias.find(c => c.value === categoria)
    return cat ? cat.label : categoria
  }

  const getCategoriaColor = (categoria) => {
    switch (categoria) {
      case 'ley': return 'error'
      case 'decreto': return 'warning'
      case 'resolucion': return 'primary'
      case 'circular': return 'info'
      case 'directiva': return 'secondary'
      case 'acuerdo': return 'success'
      case 'norma_tecnica': return 'primary'
      case 'lineamiento': return 'info'
      case 'politica': return 'warning'
      default: return 'default'
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    setFormData({ ...formData, archivo: file })
  }

  const filteredDocumentos = documentos.filter(doc => {
    const matchesSearch = doc.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.entidad_emisora.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategoria = !selectedCategoria || doc.categoria === selectedCategoria
    return matchesSearch && matchesCategoria
  })

  if (loading) {
    return (
      <Box>
        <Typography>Cargando normatividad...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Normatividad del Sistema
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Nuevo Documento
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar documentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={selectedCategoria}
              label="Categoría"
              onChange={(e) => setSelectedCategoria(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {categorias.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {filteredDocumentos.map((doc) => (
          <Grid item xs={12} md={6} lg={4} key={doc.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div" gutterBottom>
                      {doc.titulo}
                    </Typography>
                    <Chip
                      label={getCategoriaLabel(doc.categoria)}
                      color={getCategoriaColor(doc.categoria)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {doc.descripcion}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Número: {doc.numero} | Fecha: {new Date(doc.fecha_expedicion).toLocaleDateString()}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Entidad: {doc.entidad_emisora}
                  </Typography>
                </Box>

                {doc.archivo_nombre && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PictureAsPdf sx={{ fontSize: 16, color: 'error.main' }} />
                    <Typography variant="caption">
                      {doc.archivo_nombre} ({formatFileSize(doc.archivo_tamano)})
                    </Typography>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between' }}>
                <Box>
                  {doc.archivo_url && (
                    <IconButton
                      size="small"
                      onClick={() => handleViewPdf(doc)}
                      title="Ver documento"
                      color="primary"
                    >
                      <Visibility />
                    </IconButton>
                  )}
                  {doc.archivo_url && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        const link = document.createElement('a')
                        link.href = doc.archivo_url
                        link.download = doc.archivo_nombre
                        link.click()
                      }}
                      title="Descargar"
                      color="secondary"
                    >
                      <Download />
                    </IconButton>
                  )}
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(doc)}
                    title="Editar"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(doc)}
                    title="Eliminar"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}

        {filteredDocumentos.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No se encontraron documentos normativos
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedDoc ? 'Editar Documento' : 'Nuevo Documento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Título del Documento"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              required
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={formData.categoria}
                  label="Categoría"
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                >
                  {categorias.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Número"
                value={formData.numero}
                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                sx={{ flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Fecha de Expedición"
                type="date"
                value={formData.fecha_expedicion}
                onChange={(e) => setFormData({ ...formData, fecha_expedicion: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />

              <TextField
                label="Entidad Emisora"
                value={formData.entidad_emisora}
                onChange={(e) => setFormData({ ...formData, entidad_emisora: e.target.value })}
                sx={{ flex: 1 }}
              />
            </Box>

            {!selectedDoc && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Método de carga del documento
                  </Typography>
                  <ToggleButtonGroup
                    value={uploadMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setUploadMode(newMode)}
                    fullWidth
                    size="small"
                  >
                    <ToggleButton value="file">
                      <AttachFile sx={{ mr: 1 }} />
                      Subir Archivo
                    </ToggleButton>
                    <ToggleButton value="url">
                      <Link sx={{ mr: 1 }} />
                      Desde URL
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </>
            )}

            {uploadMode === 'file' && (
              <Box>
                <input
                  accept=".pdf"
                  style={{ display: 'none' }}
                  id="archivo-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="archivo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    fullWidth
                  >
                    {formData.archivo ? formData.archivo.name : 'Seleccionar archivo PDF'}
                  </Button>
                </label>
                {uploading && (
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ mt: 1 }} 
                  />
                )}
              </Box>
            )}

            {uploadMode === 'url' && !selectedDoc && (
              <Box>
                <TextField
                  fullWidth
                  label="URL del PDF"
                  value={formData.pdf_url}
                  onChange={(e) => setFormData({ ...formData, pdf_url: e.target.value })}
                  placeholder="https://ejemplo.com/documento.pdf"
                  helperText="Ingrese la URL directa de un archivo PDF. El sistema descargará y validará el archivo automáticamente."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Link />
                      </InputAdornment>
                    ),
                  }}
                />
                {uploading && (
                  <LinearProgress sx={{ mt: 1 }} />
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={
              !formData.titulo || 
              !formData.descripcion || 
              uploading ||
              (!selectedDoc && uploadMode === 'file' && !formData.archivo) ||
              (!selectedDoc && uploadMode === 'url' && !formData.pdf_url)
            }
          >
            {uploading ? (uploadMode === 'url' ? 'Descargando...' : 'Subiendo...') : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de vista previa del PDF */}
      <Dialog 
        open={openPdfModal} 
        onClose={() => setOpenPdfModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PictureAsPdf color="error" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">
                {selectedPdfDoc?.titulo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedPdfDoc?.categoria && getCategoriaLabel(selectedPdfDoc.categoria)} 
                {selectedPdfDoc?.numero && ` • ${selectedPdfDoc.numero}`}
                {selectedPdfDoc?.fecha_expedicion && ` • ${selectedPdfDoc.fecha_expedicion}`}
              </Typography>
            </Box>
            <Button
              startIcon={<Download />}
              onClick={() => {
                if (selectedPdfDoc?.archivo_url) {
                  const link = document.createElement('a')
                  link.href = selectedPdfDoc.archivo_url
                  link.download = selectedPdfDoc.archivo_nombre || selectedPdfDoc.titulo
                  link.click()
                }
              }}
              size="small"
            >
              Descargar
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {selectedPdfDoc?.archivo_url ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {selectedPdfDoc.descripcion && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Descripción:</strong> {selectedPdfDoc.descripcion}
                  </Typography>
                  {selectedPdfDoc.entidad_emisora && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Entidad Emisora:</strong> {selectedPdfDoc.entidad_emisora}
                    </Typography>
                  )}
                </Box>
              )}
              <Box sx={{ flexGrow: 1, position: 'relative' }}>
                <iframe
                  src={`${selectedPdfDoc.archivo_url}#toolbar=1&navpanes=1&scrollbar=1`}
                  width="100%"
                  height="100%"
                  style={{ 
                    border: 'none',
                    minHeight: '500px'
                  }}
                  title={`Vista previa: ${selectedPdfDoc.titulo}`}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px',
              flexDirection: 'column',
              gap: 2
            }}>
              <PictureAsPdf sx={{ fontSize: 64, color: 'grey.400' }} />
              <Typography color="text.secondary">
                No hay documento disponible para mostrar
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPdfModal(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default NormatividadAdmin